// Backups and recovery in the LexSide account (doc/20261013-backup-and-recovery.md §4.6, X-13;
// design card 46): the account's zones with backups, its recovery contacts, and any escrow
// release in progress. And the page a notice's cancel link opens, which needs no sign-in.

import { ClockCircleOutlined, InfoCircleOutlined, MailOutlined, SafetyCertificateOutlined, UserOutlined } from '@ant-design/icons';
import { LanguageSwitch } from '../LanguageSwitch';
import { Alert, Button, Card, Empty, Form, Input, List, Popconfirm, Space, Spin, Tag, Typography } from 'antd';
import type { TFunction } from 'i18next';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trans } from '../i18n/Trans';
import { i18n } from '../i18n';
import { useFormat, type Formatters } from '../i18n/format';
import type { Account } from './api';
import { ApiError, CONTROL_API_URL } from './api';
import { holdComposingEnter } from './composition';

const { Title, Paragraph, Text } = Typography;

interface Contact { readonly id: string; readonly kind: 'email' | 'mobile'; readonly value: string; readonly verifiedAt: number | null }
interface BackupZone {
  readonly zoneId: string; readonly name: string; readonly topology: string; readonly destination: string; readonly state: string;
  readonly lastSuccessAt: number | null; readonly escrowState: 'off' | 'active' | 'suspended';
}
interface Release {
  readonly releaseId: string; readonly zoneId: string; readonly state: string;
  readonly requestedAt: number; readonly releaseAfter: number | null; readonly releasedAt: number | null; readonly cancelledAt: number | null;
}

async function recoveryRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${CONTROL_API_URL}${path}`, {
    ...init, credentials: 'include', redirect: 'error',
    headers: init.body === undefined ? init.headers : { 'content-type': 'application/json', ...init.headers },
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null) as { error?: unknown } | null;
    throw new ApiError(response.status, typeof body?.error === 'string' ? body.error : 'request_failed');
  }
  return (response.status === 204 ? undefined : await response.json()) as T;
}

type RecoveryT = TFunction<'console'>;
// eslint-disable-next-line i18next/no-literal-string -- backup destination codes
const DESTINATIONS = ['us', 'sg', 'cn'] as const;
function destinationName(t: RecoveryT, destination: string): string {
  const known = DESTINATIONS.find((item) => item === destination);
  return known === undefined ? destination : t(`recovery.destination.${known}`);
}
const at = (format: Formatters, value: number) => format.date(new Date(value), { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' });

function contactError(t: RecoveryT, caught: unknown): string {
  if (!(caught instanceof ApiError)) return t('recovery.error.failed');
  switch (caught.code) {
    case 'mobile_unavailable': return t('recovery.error.mobileUnavailable');
    case 'contact_is_sign_in_email': return t('recovery.error.signInEmail');
    case 'invalid_contact': return t('recovery.error.invalidContact');
    case 'too_many_contacts': return t('recovery.error.tooMany');
    case 'invalid_code': return t('recovery.error.invalidCode');
    default: return t('recovery.error.failed');
  }
}

/** /control/recovery: the account's backups, recovery contacts and escrow releases. */
export function RecoveryPage({ account, onUnauthorized }: { readonly account: Account; readonly onUnauthorized: () => void }) {
  const { t } = useTranslation('console');
  const format = useFormat();
  const [loading, setLoading] = useState(true);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [zones, setZones] = useState<BackupZone[]>([]);
  const [blockedUntil, setBlockedUntil] = useState<number | null>(null);
  const [releases, setReleases] = useState<Release[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState<{ id: string; value: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [form] = Form.useForm<{ value: string }>();
  const [codeForm] = Form.useForm<{ code: string }>();

  const fail = useCallback((caught: unknown, message?: string) => {
    // A wrong or expired contact code is also a 401: the session is fine, the code is not.
    if (caught instanceof ApiError && caught.status === 401 && caught.code !== 'invalid_code') onUnauthorized();
    else setError(message ?? contactError(i18n.getFixedT(null, 'console'), caught));
  }, [onUnauthorized]);

  // Refreshes can overlap (polling, focus, a mutation): only the latest one's answer is shown.
  const latest = useRef(0);
  const load = useCallback(async () => {
    const generation = ++latest.current;
    try {
      const [listedContacts, listedBackups, listedReleases] = await Promise.all([
        recoveryRequest<{ contacts: Contact[] }>('/v1/account/contacts'),
        recoveryRequest<{ zones: BackupZone[]; recoveryBlockedUntil: number | null }>('/v1/backups'),
        recoveryRequest<{ releases: Release[] }>('/v1/escrow/releases'),
      ]);
      if (generation !== latest.current) return;
      setContacts(listedContacts.contacts);
      setZones(listedBackups.zones);
      setBlockedUntil(listedBackups.recoveryBlockedUntil);
      setReleases(listedReleases.releases);
    } catch (caught) {
      if (generation === latest.current) fail(caught, i18n.t('console:recovery.loadFailed'));
    } finally {
      if (generation === latest.current) setLoading(false);
    }
  }, [fail]);
  useEffect(() => {
    // A recovery can be requested from another machine at any moment: keep the page current.
    void load();
    const timer = window.setInterval(() => { void load(); }, 30_000);
    const refresh = () => { if (document.visibilityState === 'visible') void load(); };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [load]);

  const run = async (action: () => Promise<unknown>) => {
    setBusy(true); setError(null);
    try { await action(); await load(); } catch (caught) { fail(caught); } finally { setBusy(false); }
  };

  const zoneName = (id: string) => zones.find((zone) => zone.zoneId === id)?.name ?? t('recovery.unknownZone');
  // eslint-disable-next-line i18next/no-literal-string -- release states
  const open = releases.filter((release) => ['notifying', 'holding', 'releasing'].includes(release.state));
  const verified = contacts.filter((contact) => contact.verifiedAt !== null);

  if (loading) return <div className="cp-page"><Spin /></div>;
  return <div className="cp-page cp-recovery">
    <div className="cp-page-heading"><div>
      <Title level={1}>{t('shell.recovery')}</Title>
      <Paragraph type="secondary">{t('recovery.intro')}</Paragraph>
    </div></div>
    {error && <Alert type="error" showIcon message={error} closable onClose={() => setError(null)} />}

    {open.map((release) => <Card key={release.releaseId} className="cp-recovery-release"
      title={<Space><ClockCircleOutlined />{release.state === 'holding' && release.releaseAfter !== null
        ? t('recovery.release.releasedAt', { zone: zoneName(release.zoneId), when: at(format, release.releaseAfter) })
        : release.state === 'releasing' ? t('recovery.release.releasing', { zone: zoneName(release.zoneId) }) : t('recovery.release.telling', { zone: zoneName(release.zoneId) })}</Space>}>
      <Paragraph><Trans t={t} i18nKey="recovery.release.requested" values={{ zone: zoneName(release.zoneId), when: at(format, release.requestedAt) }} components={{ b: <b /> }} /></Paragraph>
      <List size="small" dataSource={[
        { done: true, title: t('recovery.step.verifiedTitle'), text: t('recovery.step.verified') },
        { done: release.state !== 'notifying', title: t('recovery.step.toldTitle'), text: t('recovery.step.told') },
        { done: false, title: t('recovery.step.holdTitle'), text: t('recovery.step.hold') },
        { done: false, title: t('recovery.step.releasedTitle'), text: t('recovery.step.released') },
      ]} renderItem={(item) => <List.Item><List.Item.Meta avatar={item.done ? <Tag color="success">{t('recovery.step.done')}</Tag> : <Tag>{t('recovery.step.waiting')}</Tag>} title={item.title} description={item.text} /></List.Item>} />
      {release.state === 'releasing'
        ? <Paragraph type="secondary">{t('recovery.release.tooLate')}</Paragraph>
        : <Popconfirm title={t('recovery.cancel.title')} description={t('recovery.cancel.detail')} okText={t('recovery.cancel.ok')} cancelText={t('recovery.cancel.keep')}
          onConfirm={() => void run(() => recoveryRequest('/v1/escrow/releases/cancel', { method: 'POST', body: JSON.stringify({ releaseId: release.releaseId }) }))}>
          <Button danger loading={busy}>{t('recovery.cancel.button')}</Button>
        </Popconfirm>}
    </Card>)}

    <Card title={<Space><MailOutlined />{t('recovery.contacts.title')}</Space>}
      extra={verified.length > 0 ? <Tag color="success">{t('recovery.contacts.escrowReady')}</Tag> : <Tag color="warning">{t('recovery.contacts.noSecond')}</Tag>}>
      <List size="small" dataSource={[{ id: 'sign-in', value: account.email, note: t('recovery.contacts.signInEmail'), removable: false, verifiedAt: 0 as number | null }, ...contacts.map((contact) => ({
        id: contact.id, value: contact.value, removable: true, verifiedAt: contact.verifiedAt,
        note: contact.verifiedAt === null ? t('recovery.contacts.waitingForCode') : t('recovery.contacts.verified', { date: format.date(new Date(contact.verifiedAt)) }),
      }))]} renderItem={(item) => <List.Item actions={item.removable ? [
        ...(item.verifiedAt === null && adding?.id !== item.id
          ? [<Button key="code" type="link" onClick={() => setAdding({ id: item.id, value: item.value })}>{t('recovery.contacts.enterCode')}</Button>] : []),
        <Popconfirm key="remove" title={t('recovery.contacts.removeTitle')} description={t('recovery.contacts.removeDetail')}
          onConfirm={() => void run(() => recoveryRequest(`/v1/account/contacts/${encodeURIComponent(item.id)}`, { method: 'DELETE' }))}>
          <Button type="link" danger>{t('recovery.contacts.remove')}</Button>
        </Popconfirm>,
      ] : []}><List.Item.Meta avatar={item.removable ? <MailOutlined /> : <UserOutlined />} title={item.value} description={item.note} /></List.Item>} />
      <Paragraph type="secondary">{t('recovery.contacts.explain')}</Paragraph>
      {blockedUntil !== null && <Alert type="info" showIcon message={t('recovery.contacts.blocked', { when: at(format, blockedUntil) })} />}
      {adding === null
        ? <Form form={form} layout="inline" onFinish={({ value }) => void run(async () => {
          const created = await recoveryRequest<{ id: string }>('/v1/account/contacts', { method: 'POST', body: JSON.stringify({ kind: 'email', value }) });
          setAdding({ id: created.id, value }); form.resetFields();
        })}>
          <Form.Item name="value" rules={[{ required: true, type: 'email', message: t('recovery.contacts.emailRequired') }]}>
            <Input type="email" placeholder={t('recovery.contacts.another')} aria-label={t('recovery.contacts.another')} onKeyDown={holdComposingEnter} />
          </Form.Item>
          <Button htmlType="submit" loading={busy}>{t('recovery.contacts.add')}</Button>
        </Form>
        : <Form form={codeForm} layout="inline" onFinish={({ code }) => void run(async () => {
          await recoveryRequest(`/v1/account/contacts/${encodeURIComponent(adding.id)}/verify`, { method: 'POST', body: JSON.stringify({ code }) });
          setAdding(null); codeForm.resetFields();
        })}>
          <Text>{t('recovery.contacts.codeSent', { address: adding.value })}</Text>
          <Form.Item name="code" rules={[{ required: true, pattern: /^\d{6}$/u, message: t('recovery.contacts.codeRequired') }]}>
            <Input inputMode="numeric" maxLength={6} placeholder={t('recovery.contacts.code')} aria-label={t('recovery.contacts.code')} onKeyDown={holdComposingEnter} />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={busy}>{t('recovery.contacts.verify')}</Button>
          <Button type="link" onClick={() => setAdding(null)}>{t('recovery.contacts.later')}</Button>
          <Text type="secondary">{t('recovery.contacts.codeHint')}</Text>
        </Form>}
    </Card>

    <Card title={<Space><SafetyCertificateOutlined />{t('recovery.zones.title')}</Space>}>
      {zones.length === 0
        ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('recovery.zones.empty')} />
        : <List size="small" dataSource={zones} renderItem={(zone) => <List.Item>
          <List.Item.Meta title={zone.name} description={[
            zone.topology === 'firm' ? t('zone.topology.firm') : t('zone.topology.laptop'),
            destinationName(t, zone.destination),
            zone.lastSuccessAt === null ? t('recovery.zones.noBackup') : t('recovery.zones.lastBackup', { when: at(format, zone.lastSuccessAt) }),
            // eslint-disable-next-line i18next/no-literal-string -- the separator between the zone's facts
          ].join(' · ')} />
          <Space>
            {zone.state === 'failing' ? <Tag color="error">{t('recovery.zones.failing')}</Tag> : <Tag color="success">{t('recovery.zones.backingUp')}</Tag>}
            {zone.escrowState === 'active' ? <Tag>{t('recovery.zones.escrowActive')}</Tag> : zone.escrowState === 'suspended' ? <Tag color="warning">{t('recovery.zones.escrowSuspended')}</Tag> : <Tag>{t('recovery.zones.phraseOnly')}</Tag>}
          </Space>
        </List.Item>} />}
      <Paragraph type="secondary">
        <Trans t={t} i18nKey="recovery.zones.restoreHint" components={{ code: <Text code /> }} />
      </Paragraph>
    </Card>
  </div>;
}

/** /control/recovery/cancel#<token>: the cancel link in a recovery notice. No sign-in. */
export function RecoveryCancelPage({ onOpenAccount }: { readonly onOpenAccount: () => void }) {
  const { t } = useTranslation('console');
  const [token] = useState(() => window.location.hash.slice(1));
  const [state, setState] = useState<'ready' | 'working' | 'cancelled' | 'released' | 'invalid' | 'failed'>(() => token === '' ? 'invalid' : 'ready');
  useEffect(() => {
    // The token stays out of the address bar's history once read.
    if (window.location.hash !== '') window.history.replaceState(null, '', window.location.pathname);
  }, []);
  const cancel = async () => {
    setState('working');
    try {
      await recoveryRequest('/v1/escrow/releases/cancel', { method: 'POST', body: JSON.stringify({ token }) });
      setState('cancelled');
    } catch (caught) {
      setState(caught instanceof ApiError && caught.status === 404 ? 'invalid'
        : caught instanceof ApiError && caught.code === 'release_already_released' ? 'released' : 'failed');
    }
  };
  return <div className="cp-recovery-cancel">
    <div className="cp-auth-theme"><LanguageSwitch className="cp-theme-button" /></div>
    <Card className="cp-recovery-cancel-card">
      {state === 'cancelled' ? <>
        <Title level={2}>{t('recovery.cancelPage.cancelledTitle')}</Title>
        <Paragraph>{t('recovery.cancelPage.cancelled')}</Paragraph>
        <Alert type="warning" showIcon icon={<InfoCircleOutlined />} message={t('recovery.cancelPage.warning')}
          description={t('recovery.cancelPage.warningDetail')} />
        <Button block onClick={onOpenAccount}>{t('recovery.cancelPage.openAccount')}</Button>
      </> : state === 'released' ? <>
        <Title level={2}>{t('recovery.cancelPage.releasedTitle')}</Title>
        <Paragraph>{t('recovery.cancelPage.released')}</Paragraph>
      </> : state === 'invalid' ? <>
        <Title level={2}>{t('recovery.cancelPage.invalidTitle')}</Title>
        <Paragraph>{t('recovery.cancelPage.invalid')}</Paragraph>
        <Button block onClick={onOpenAccount}>{t('recovery.cancelPage.openAccount')}</Button>
      </> : <>
        <Title level={2}>{t('recovery.cancel.title')}</Title>
        <Paragraph>{t('recovery.cancelPage.ask')}</Paragraph>
        {state === 'failed' && <Alert type="error" showIcon message={t('recovery.cancelPage.failed')} />}
        <Button type="primary" danger block loading={state === 'working'} onClick={() => void cancel()}>{t('recovery.cancelPage.confirm')}</Button>
      </>}
    </Card>
  </div>;
}
